import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Row } from "@tanstack/react-table";
import { Category } from "types";
import DataTable from "components/DataTable";
import PaginationControls from "components/PaginationControls";
import { ChangeEvent, KeyboardEvent } from "react";
import Select, { components } from "react-select";
import { InstallationStatusBadge } from "components/Installationstatus";
import TablePaginationControls from "components/TableandmobilePaginationcontrol"
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import { ViewIconButton, EditIconButton, RemovalIconButton } from "components/DatatablesActionIcons";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";

import {
  ExportAsExcel,
} from "@siamf/react-export";

import { Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { getAuthCookie } from "../../utils/authCookies";
import { formatPaymentMethodLabel } from "../../utils/paymentMethodLabel";

interface ExportRow {
  "Order ID": string;
  "Order Date": string; // or Date, if you want to keep as Date object
  "Order Status": string;
  "Payment Status": string;
  "Buyer Type": string;
  "First Name": string;
  "Last Name": string;
  "Email": string;
  "Phone": string;
  "Billing Address": string;
  "Shipping Address": string;
  "Item Details": string; // If you want to export as JSON or a string
  "Customization Information": string;
  "Shipping Method": string;
  "Sub Total": number;
  "Shipping": number;
  "Tax": number;
  "Total": number;
}

function fuzzyTextFilter<TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: string
) {
  const raw = row.getValue(columnId);
  const value = (typeof raw === "boolean"
    ? raw
      ? "Active"
      : "Inactive"
    : String(raw)
  ).toLowerCase();

  return value.includes(filterValue.toLowerCase());
}

const DATE_OPTIONS = [
  { label: "Today", value: "today" },
  { label: "Yesterday", value: "yesterday" },
  { label: "Last 15 Days", value: "last_15_days" },
  { label: "Last 30 Days", value: "last_30_days" },
  { label: "Custom", value: "custom" },
];

const ORDER_STATUS_OPTIONS = [
  { value: "", label: "Order Status" },
  { value: "100", label: "Draft" },
  { value: "200", label: "Finalized" },
  { value: "300", label: "Scheduled" },
  { value: "400", label: "In Progress" },
  { value: "500", label: "Completed" },
  { value: "600", label: "Cancelled" }
];

const PAYMENT_STATUS_OPTIONS = [
  { value: "", label: "Payment Status" },
  { value: "Paid", label: "Paid" },
  { value: "Partial Payment", label: "Partial Payment" },
  { value: "Pending", label: "Pending" },
  { value: "Overdue", label: "Overdue" }
];

const PRIORITY_OPTIONS = [
  { value: "", label: "Priority" },
  { value: "1", label: "High" },
  { value: "2", label: "Medium" },
  { value: "3", label: "Low" }
];

const STATUS_MAP: { [key: number]: string } = {
  100: "Draft",
  200: "Finalized",
  300: "Scheduled",
  400: "In Progress",
  500: "Completed",
  600: "Cancelled",
};

const DropdownIndicator = (props: any) => {
  if (props.selectProps.isDisabled) return null;
  return (
    <components.DropdownIndicator {...props}>
      <i className="ri-arrow-down-s-line fs-5" style={{ color: "#6c757d" }} />
    </components.DropdownIndicator>
  );
};

const customSelectStyles = {
  indicatorsContainer: (p: any, s: any) => ({ ...p, display: s.isDisabled ? "none" : "flex" }),
  dropdownIndicator: (p: any, s: any) => ({ ...p, display: s.isDisabled ? "none" : "flex" }),
  indicatorSeparator: () => ({ display: "none" }),
  control: (p: any, s: any) => ({
    ...p,
    backgroundColor: s.isDisabled ? "#f2f2f2" : p.backgroundColor,
    cursor: s.isDisabled ? "not-allowed" : "pointer",
    opacity: 1,
  }),
  singleValue: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
  valueContainer: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
  input: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color, WebkitTextFillColor: s.isDisabled ? "#000" : undefined }),
  placeholder: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
  menu: (provided: any) => ({ ...provided, zIndex: 9999, position: "absolute", top: "100%", left: 0, right: 0 }),
  menuList: (provided: any) => ({ ...provided, maxHeight: "220px" }),
};

const columnHelper = createColumnHelper<any>();

const AgentJobsInstallations = () => {
  const [orders, setOrders] = useState<any>([]);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
  const location = useLocation();
  const navigate = useNavigate();

  function getInstallationRowId(order: any): string {
    return String(order?._id ?? order?.id ?? order?.order_id ?? "");
  }

  const [rowCount, setRowCount] = useState(0);
  const [exportRows, setExportRows] = useState<ExportRow[]>([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [globalFilter, setGlobalFilter] = useState("");
  const [searchInput, setSearchInput] = useState<string>(globalFilter ?? "");
  const searchInputRef = useRef<HTMLInputElement>(null);
  // const [orderStatusFilter, setOrderStatusFilter] = useState("all");
  // const [productionStatusFilter, setProductionStatusFilter] = useState("all");
  // const [paymentStatusFilter, setPaymentStatusFilter] = useState("all");
  const [datePreset, setDatePreset] = useState(DATE_OPTIONS[3]);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const { minDate, maxDate } = useMemo(() => {
    const now = new Date();
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(now.getMonth() - 6);
    return {
      minDate: sixMonthsAgo.toISOString().split("T")[0],
      maxDate: now.toISOString().split("T")[0],
    };
  }, []);

  useEffect(() => {
    if (datePreset.value === "custom") {
      if (customFrom && (customFrom < minDate || customFrom > maxDate)) setCustomFrom("");
      if (customTo && (customTo < minDate || customTo > maxDate)) setCustomTo("");
    }
  }, [datePreset.value, minDate, maxDate]);

  const [filtersOpen, setFiltersOpen] = useState(false);

  const [orderStatusFilter, setOrderStatusFilter] = useState(ORDER_STATUS_OPTIONS[0]);
  // const [productionStatusFilter, setProductionStatusFilter] = useState(PRODUCTION_STATUS_OPTIONS[0]);
  const [paymentStatusFilter, setPaymentStatusFilter] = useState(PAYMENT_STATUS_OPTIONS[0]);
  const [priorityFilter, setPriorityFilter] = useState(PRIORITY_OPTIONS[0]);


  const fetchOrders = async () => {
    // Only show loading on initial load, not during search
    if (pagination.pageIndex === 0 && !globalFilter) {
      setLoading(true);
    }
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/installation_orders/datatable`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            page: pagination.pageIndex + 1,
            limit: pagination.pageSize,
            search: globalFilter,
            date_range: {
              type: datePreset.value,
              from: datePreset.value === "custom" ? customFrom : "",
              to: datePreset.value === "custom" ? customTo : "",
            },
            filters: {
              status: orderStatusFilter.value || undefined,
              // production_status: productionStatusFilter.value || undefined,
              payment_status: paymentStatusFilter.value === "all" ? undefined : paymentStatusFilter.value || undefined,
              priority: (priorityFilter.value || undefined)
            },
            sort: {
              field: "created_at",
              order: "desc",
            },
            // Add filters and date_range if needed
          })
        }
      );
      if (!res.ok) throw new Error("Failed to fetch installation orders.");
      const data = await res.json();
      console.log("Fetched Installation Orders:", data?.data || []);
      setOrders(data?.data || []);
      setRowCount(data?.total || 0);
    } catch (err: any) {
      setOrders([]);
      setRowCount(0);
      toast.error(err?.message || "Failed to fetch installation orders");
    } finally {
      setLoading(false);
      // Maintain cursor focus in search input after loading
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }
  };

  useEffect(() => {
    // const fetchOrders = async () => {
    //   try {
    //     const response = await fetch(
    //       `${process.env.REACT_APP_API_URL}/installation_orders/source/${userId}`,
    //       {
    //         method: "GET",
    //         headers: {
    //           Authorization: `Bearer ${token}`,
    //           "Content-Type": "application/json",
    //         },
    //       }
    //     );
    //     const data = await response.json();
    //     console.log("Installation Orders:", data);
    //     if (!response.ok) throw new Error(data.message || "Failed to fetch installation orders");
    //     setInstallationOrders(data);
    //     setRowCount(1);
    //   } catch (err) {
    //     setError(
    //       err instanceof Error ? err.message : "An unknown error occurred"
    //     );
    //   } finally {
    //     setLoading(false);
    //   }
    // };
    fetchOrders();
  }, [token, pagination, globalFilter]);

  useEffect(() => {
    const statusMap = {
      100: "Draft",
      200: "Pending Approval",
      300: "Scheduled",
      400: "In Progress",
      500: "Completed",
      // add more as needed
    };

    const getAddressString = (addr: any) =>
      [addr?.addressLine1, addr?.city, addr?.state, addr?.zip, addr?.country]
        .filter(Boolean)
        .join(", ");

    const getFullName = (name: string) => {
      if (!name) return ["", ""];
      const parts = name.split(" ");
      return [parts[0] || "", parts.slice(1).join(" ") || ""];
    };

    const rows = orders.map((order: any) => {
      const [firstName, lastName] = getFullName(order.user_name);

      // Item Details as a single string, each line item on a new line
      const itemDetails = (order.line_items || [])
        .map((item: any) =>
          `${item.product_name} (SKU: ${item.sku}) x${item.quantity}`
        )
        .join("\n");

      // Customization Info (optional, based on your schema)
      const customizationInfo = (order.additional_instructions || [])
        .map((i: any) => i.value)
        .filter(Boolean)
        .join("; ");

      return {
        "Order ID": order.order_number || order._id,
        "Order Date": order.created_at
          ? new Date(order.created_at).toLocaleDateString()
          : "",
        "Order Status": statusMap[order.status as keyof typeof statusMap] || order.status || "",
        "Payment Status": order.payment_status || "",
        "Buyer Type": order.user_type || "",
        "First Name": firstName,
        "Last Name": lastName,
        "Email": order.user_email || "",
        "Phone": order.user_phone || "",
        "Billing Address": getAddressString(order.billing_address),
        "Shipping Address": getAddressString(order.installation_details?.installation_address),
        "Item Details": itemDetails,
        "Customization Information": customizationInfo,
        "Shipping Method": order.shipping_method || "",
        "Sub Total": order.subtotal_amount != null ? order.subtotal_amount.toFixed(2) : "",
        "Shipping": order.installation_cost != null ? order.installation_cost.toFixed(2) : "",
        "Tax": order.tax_details?.tax_amount != null
          ? order.tax_details.tax_amount.toFixed(2)
          : order.installation_details?.tax_details?.tax_amount != null
            ? order.installation_details.tax_details.tax_amount.toFixed(2)
            : "",
        "Total": order.total_amount != null ? order.total_amount.toFixed(2) : "",
      };
    });

    setExportRows(rows);
  }, [orders]);

  const PRIORITY_LABELS: { [key: number]: string } = {
    1: "High",
    2: "Medium",
    3: "Low",
  };

  // Columns
  const columns = useMemo(() => [
    // columnHelper.display({
    //   id: "sno",
    //   header: () => <div className="th-text S-No">S.No.</div>,
    //   cell: (info) => (
    //     <div className="text-sm S-No">
    //       {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
    //     </div>
    //   ),
    // }),
    columnHelper.accessor("order_number", {
      id: "order_number",
      header: () => <div className="th-text S-No">Order ID</div>,
      cell: (info) => {
        const row = info.row.original;
        // pick whichever your data uses:
        const orderId =
          (row as any).id ?? (row as any)._id ?? (row as any).order_id;

        return (
          <Link
          to={`/agent/jobs/viewInstallation/${orderId}`} state={{ type: "View" }}
            className="text-sm S-No order-link-1"
          >
            {info.getValue() ?? ""}
          </Link>
        );
      },
      filterFn: fuzzyTextFilter,
      enableGlobalFilter: true,
    }),
    // columnHelper.accessor("order_number", {
    //   header: () => <div className="th-text ">Order ID</div>,
    //   cell: (info) => <div className="text-sm">{info.getValue()}</div>,
    // }),
    columnHelper.accessor("created_at", {
      header: () => <div className="th-text">Created Date</div>,
      cell: (info) => (
        <div className="text-sm">
          {new Date(info.getValue()).toLocaleDateString()}
        </div>
      ),
    }),
    // Brokerage Firm
    columnHelper.accessor(row =>
      row.brokerage_firm_name || row.brokerage_firm_admin || "—",
      {
        id: "brokerage_firm_name",
        header: () => <div className="th-text">Brokerage Firm</div>,
        cell: info => <div className="text-sm">{info.getValue()}</div>,
      }
    ),
    // Office
    columnHelper.accessor(row =>
      (row.office && typeof row.office === "object" && row.office.officeName)
        ? row.office.officeName
        : (typeof row.office === "string" ? row.office : "—"),
      {
        id: "office",
        header: () => <div className="th-text">Office</div>,
        cell: info => <div className="text-sm">{info.getValue()}</div>,
      }
    ),
    // Agent
    columnHelper.accessor("agent_name", {
      header: () => <div className="th-text">Agent</div>,
      cell: info => <div className="text-sm">{info.getValue() || "—"}</div>,
    }),
    // Installer
    columnHelper.accessor(row =>
      row.installation_details?.installer_name || "—",
      {
        id: "installer_name",
        header: () => <div className="th-text">Installer</div>,
        cell: info => <div className="text-sm">{info.getValue()}</div>,
      }
    ),
    // Installation Date
    columnHelper.accessor(row =>
      row.installation_details?.scheduled_date
        ? new Date(row.installation_details.scheduled_date).toLocaleDateString()
        : "—",
      {
        id: "installation_date",
        header: () => <div className="th-text">Installation Date</div>,
        cell: info => <div className="text-sm">{info.getValue()}</div>,
      }
    ),
    // Payment Status column
    columnHelper.accessor("payment_status", {
      header: () => <div className="th-text">Payment Status</div>,
      cell: info => (
        <div className="text-sm">
          <InstallationStatusBadge value={info.getValue() as string} type="payment" />
        </div>
      ),
    }),
    columnHelper.accessor(
      (row) => formatPaymentMethodLabel(row.payment_method),
      {
        id: "payment_method",
        header: () => <div className="th-text">Payment Method</div>,
        cell: info => <div className="text-sm">{info.getValue()}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }
    ),

    // Order Status column
    columnHelper.accessor("status", {
      header: () => <div className="th-text">Status</div>,
      cell: info => (
        <div className="text-sm">
          <InstallationStatusBadge value={info.getValue() as number} type="order" />
        </div>
      ),
    }),
    // Priority column
    columnHelper.accessor(
      row => (row.priority !== undefined ? PRIORITY_LABELS[row.priority] : undefined),
      {
        id: "priority",
        header: () => <div className="th-text">Priority</div>,
        cell: info => (
          <div className="text-sm">
            <InstallationStatusBadge value={info.getValue() as string} type="priority" />
          </div>
        ),
      }
    ),
    // // Payment Status
    // columnHelper.accessor("payment_status", {
    //   header: () => <div className="th-text">Payment Status</div>,
    //   cell: info => <div className="text-sm">{info.getValue() || "—"}</div>,
    // }),
    // // Status
    // columnHelper.accessor("status", {
    //   header: () => <div className="th-text">Status</div>,
    //   cell: info => <div className="text-sm">{STATUS_MAP[info.getValue()] || "Unknown"}</div>,
    // }),
    // columnHelper.accessor(row => (row.priority !== undefined ? PRIORITY_LABELS[row.priority] : "—"), {
    //   id: "priority",
    //   header: () => <div className="th-text">Priority</div>,
    //   cell: info => <div className="text-sm">{info.getValue()}</div>,
    // }),
    // Actions
    columnHelper.display({
      id: "actions",
      header: () => <div className="th-text">Actions</div>,
      cell: (info) => {
        const order = info.row.original;
        return (
          <div className="text-sm d-flex gap-2 justify-content-center">
            <ViewIconButton
              to={`/agent/jobs/viewInstallation/${order._id}`} state={{ type: "View" }}
              title="View"
            />
            {
              order.status === 600 ? (
                ""
              ) : order.status < 200 ? (
                <EditIconButton
                  to={`/agent/jobs/viewInstallation/${order._id}`} state={{ type: "Edit" }}
                  title="Edit"
                />

              ) : order.status == 500 && (
                <RemovalIconButton

                  to={`/agent/jobs/addRemovalRequest/${order._id}`}
                  title="Removal Request"
                />
              )
            }
          </div>
        );
      }
    }),
  ], [pagination]);

  const table = useReactTable({
    data: orders,
    columns,
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    state: {
      pagination,
      globalFilter,
    },
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
  });

  const pageCount = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex;
  // if (loading) {
  //   return <div>Loading...</div>;
  // }

  return (
    <div className="content">
      <div className="d-flex justify-content-between align-items-center pb-3 mb-2 py-3">
        {/* <h4 className="mb-0 fw-bold">Orders</h4> */}
        <h4></h4>
        <div className="d-flex gap-3 align-items-center">
          <Link to="/agent/jobs/addInstallation" className="btn btn-primary  table-addbutton">
            + Add Installation
          </Link>

        </div>
      </div>
      <div className="po-header mb-4">
        <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
          <div className="po-title-wrap me-2">
            <h4 className="mb-1 po-title">Installations</h4>
            {/* <div className="po-subtitle">Manage incoming products from suppliers and brokerages</div> */}
          </div>

          {/* tools block (search + buttons) */}
          <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
            {/* Search */}
            <div className="input-group po-search ">
              <span className="input-group-text">
                <i className="ri-search-line" />
              </span>
              <input
                ref={searchInputRef}
                type="text"
                id="searchInput"
                name="searchInput"
                className="form-control"
                placeholder="Search by Order ID, Brokerage Firm, Agent"
                value={searchInput}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Enter") {
                    setPagination((prev) => ({ ...prev, pageIndex: 0 })); // :white_check_mark: Reset to page 1
                    setGlobalFilter(searchInput);
                  }
                }}
              />
            </div>

            {/* Filters toggle */}
            <button
              type="button"
              className={` po-filter-btn d-inline-flex align-items-center ${filtersOpen ? "active" : ""}`}
              onClick={() => setFiltersOpen(v => !v)}
              aria-expanded={filtersOpen}
              aria-controls="incoming-filters"
            >
              <i className="ri-filter-3-line me-2" />
              <span>Filters</span>
              <i className={`ri-arrow-down-s-line ms-1 ${filtersOpen ? "rotate-180" : ""}`} />
            </button>

            <ExportAsExcel
              data={orders}
              headers={[
                "Order ID",
                "Created Date",
                "Brokerage Firm",
                "Office",
                "Agent",
                "Installer",
                "Installation Date",
                "Payment Status",
                "Status",
                "Production Status",
                "Product Location Status",
                "Priority"
              ]}
              fileName="Installations"
            >
              {(props) => (
                <button {...props} className="export-button">
                  <i className="ri-file-excel-2-line me-2" />
                  Export to Excel
                </button>
              )}
            </ExportAsExcel>

          </div>
        </div>
        <div id="incoming-filters" className={`po-filters ${filtersOpen ? "show" : ""}`}>
          <div className="po-filters-bar d-flex">
            <div className="row g-2 g-md-3 align-items-end justify-content-md-end ms-md-auto w-100 w-md-auto">

              {/* Date Range */}
              <div className="col-12 col-md-auto">
                <label className="form-label mb-1 label-1">Date Range</label>
                <div className="po-rs min-220">
                  <Select
                    classNamePrefix="rs"
                    options={DATE_OPTIONS}
                    value={datePreset}
                    onChange={(val) => val && setDatePreset(val)}
                    components={{ DropdownIndicator }}
                    styles={customSelectStyles}
                  />
                </div>

                {datePreset?.value === "custom" && (
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    <div className="flex-fill">
                      <label className="form-label mb-1 label-1">From</label>
                      <input
                        type="date"
                        className="form-control"
                        min={minDate}
                        max={maxDate}
                        value={customFrom}
                        onChange={(e) => setCustomFrom(e.target.value)}
                      />
                    </div>
                    <div className="flex-fill">
                      <label className="form-label mb-1 label-1">To</label>
                      <input
                        type="date"
                        className="form-control"
                        min={minDate}
                        max={maxDate}
                        value={customTo}
                        onChange={(e) => setCustomTo(e.target.value)}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Order Status */}
              <div className="col-12 col-lg-auto">
                <label className="form-label mb-1 label-1">Order Status</label>
                <div className="po-rs min-220">
                  <Select
                    classNamePrefix="rs"
                    options={ORDER_STATUS_OPTIONS}
                    value={orderStatusFilter}
                    onChange={(option) => option && setOrderStatusFilter(option)}
                    isSearchable
                    components={{ DropdownIndicator }}
                    styles={customSelectStyles}
                  />
                </div>
              </div>

              {/* Payment Status */}
              <div className="col-12 col-lg-auto">
                <label className="form-label mb-1 label-1">Payment Status</label>
                <div className="po-rs min-220">
                  <Select
                    classNamePrefix="rs"
                    options={PAYMENT_STATUS_OPTIONS}
                    value={paymentStatusFilter}
                    onChange={(option) => option && setPaymentStatusFilter(option)}
                    isSearchable
                    components={{ DropdownIndicator }}
                    styles={customSelectStyles}
                  />
                </div>
              </div>

              {/* Priority */}
              <div className="col-12 col-md-auto">
                <label className="form-label mb-1 label-1">Priority</label>
                <div className="po-rs min-220">
                  <Select
                    classNamePrefix="rs"
                    options={PRIORITY_OPTIONS}
                    value={priorityFilter}
                    onChange={(option) => option && setPriorityFilter(option)}
                    isSearchable
                    components={{ DropdownIndicator }}
                    styles={customSelectStyles}
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="col-12 col-md-auto d-flex justify-content-md-end gap-2 mt-1 mt-md-0">
                <button className="btn btn-primary table-addbutton flex-fill flex-lg-grow-0" onClick={fetchOrders}>
                  Apply Filters
                </button>

                <button
                  className="btn btn-light border flex-fill flex-lg-grow-0 clear-button"
                  onClick={() => {
                    // Reset all filters to default values
                    setDatePreset(DATE_OPTIONS[3]); // "Last 30 Days" (default)
                    setCustomFrom("");
                    setCustomTo("");
                    setOrderStatusFilter(ORDER_STATUS_OPTIONS[0]); // "Order Status" (default)
                    setPaymentStatusFilter(PAYMENT_STATUS_OPTIONS[0]); // "Payment Status" (default)
                    setPriorityFilter(PRIORITY_OPTIONS[0]); // "Priority" (default)
                    setGlobalFilter(""); // Clear search
                    setSearchInput(""); // Clear search input
                    setPagination({ pageIndex: 0, pageSize: 10 }); // Reset to first page
                    // Trigger fetch with cleared filters
                    fetchOrders();
                  }}
                >
                  Clear
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>
      <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
        <div className="align-items-center bg-white border-bottom d-flex justify-content-between px-4 py-4">
          <h5 className="mb-0 ">Installations</h5>
          <span className="badge rounded-pill bg-body-tertiary text-secondary-emphasis px-3 py-2">
            {/* orders */}
          </span>

        </div>
        <hr className="mb-2" style={{ borderTop: "none" }} />
        <div className="rdt-desktop">
          <div
            className="production-scroll"
            style={{ "--rdt-min": "1200px" } as React.CSSProperties}
          >
            <table className="table production-table align-middle mb-0 table-1">
              <DesktopTableParts
                table={table}
                onRowClick={(row) => {
                  const id = getInstallationRowId(row);
                  if (!id) return;
                  navigate(`/agent/jobs/viewInstallation/${id}`, { state: { type: "View" } });
                }}
              />
            </table>
          </div>
        </div>

        {/* MOBILE (≤ 1021px) */}
        <div className="rdt-mobile ">
          <MobileCardsOnly
            table={table}
            idColId="order_link"
            titleColId="product_name"
            actionColId="actions"
            onRowClick={(row) => {
              const id = getInstallationRowId(row);
              if (!id) return;
              navigate(`/agent/jobs/viewInstallation/${id}`, { state: { type: "View" } });
            }}
          />
        </div>
      </div>
      {table.getRowModel().rows.length === 0 ? (
        <div className="text-center my-4 text-muted" style={{ fontSize: "1.1rem" }}>No records found</div>
      ) : (
        <div className="mt-4 mb-4">
          <TablePaginationControls table={table} />
        </div>)}
    </div>
  );
};

export default AgentJobsInstallations;


// <div className="row mb-3">
//           <div className="col-md-7 d-flex gap-2 justify-content-between">
//             <select className="form-select" value={orderStatusFilter} onChange={(e) => setOrderStatusFilter(e.target.value)}>
//               <option value="">Order Status</option>
//               <option value="100">Draft</option>
//               <option value="101">Confirmed</option>
//               <option value="300">Shipping</option>
//               <option value="301">Shipped</option>
//               <option value="302">Delivered</option>
//               <option value="400">Cancelled</option>
//             </select>

//             <select className="form-select" value={productionStatusFilter} onChange={(e) => setProductionStatusFilter(e.target.value)}>
//               <option value="">Production Status</option>
//               <option value="Not Started">Not Started</option>
//               <option value="In Production">In Production</option>
//               <option value="Quality Check">Quality Check</option>
//               <option value="Ready for Installation">Ready for Installation</option>
//             </select>

//             <select className="form-select" value={paymentStatusFilter} onChange={(e) => setPaymentStatusFilter(e.target.value)}>
//               <option value="">Payment Status</option>
//               <option value="Partial Payment">Partial Payment</option>
//               <option value="Paid">Paid</option>
//               <option value="Pending">Pending</option>
//             </select>
//             <select className="form-select" value={paymentStatusFilter} onChange={(e) => setPaymentStatusFilter(e.target.value)}>
//               <option value="">Priority </option>
//               <option value="1">High</option>
//               <option value="2">Medium</option>
//               <option value="3">Low</option>
//             </select>

//             <button className="btn-secondary p-2">
//               <i className="ri-filter-line"></i>
//             </button>
//           </div>

//           <div className="col-md-5 d-flex justify-content-end gap-2 h-100">
//             <div className="input-group w-50">
//               <span className="input-group-text bg-white pe-0">
//                 <i className="ri-search-line"></i>
//               </span>
//               <input
//                 type="text"
//                 className="form-control border-start-0"
//                 placeholder="Search by Order ID, Name, Email, Brokerage firm, office, Agent"
//                 value={globalFilter}
//                 onChange={(e) => setGlobalFilter(e.target.value)}
//               />
//             </div>

//             <Link
//               to="/agent/jobs/addInstallation"
//               className="btn btn-primary float-right"
//             >
//               Add Installation
//             </Link>
//           </div>
//         </div>


//   const columns = useMemo(() => {
//   return [
//     columnHelper.display({
//       id: "serial",
//       header: "S.No.",
//       cell: (info) => pagination.pageIndex * pagination.pageSize + info.row.index + 1,
//     }),
//     columnHelper.accessor(
//       (row: any) => row?.order_number || "",
//       {
//         header: "Order ID",
//         filterFn: fuzzyTextFilter,
//         enableGlobalFilter: true,
//       }),
//     columnHelper.accessor(
//       (row: any) => new Date(row.created_at).toLocaleDateString(),
//       {
//         header: "Created Date",
//         filterFn: fuzzyTextFilter,
//         enableGlobalFilter: true,
//       }),
//     columnHelper.accessor(
//       (row: any) => row?.order_number || "",
//       {
//         header: "Brokerage firm",
//         filterFn: fuzzyTextFilter,
//         enableGlobalFilter: true,
//       }),
//     columnHelper.accessor(
//       (row: any) => row?.order_number || "",
//       {
//         header: "Office",
//         filterFn: fuzzyTextFilter,
//         enableGlobalFilter: true,
//       }),
//     columnHelper.accessor(
//       (row: any) => row?.order_number || "",
//       {
//         header: "Agent",
//         filterFn: fuzzyTextFilter,
//         enableGlobalFilter: true,
//       }),

//     columnHelper.accessor(
//       (row: any) => row.installation_details?.installer_name || "-",
//       {
//         header: "Installer",
//         filterFn: fuzzyTextFilter,
//         enableGlobalFilter: true,
//       }),
//     columnHelper.accessor(
//       (row: any) => row?.installation_details?.scheduled_date
//         ? new Date(row?.installation_details.scheduled_date).toLocaleDateString()
//         : "—",
//       {
//         header: "Installation Date",
//         filterFn: fuzzyTextFilter,
//         enableGlobalFilter: true,
//       }),
//     columnHelper.accessor(
//       (row: any) => row?.payment_status || "",
//       {
//         header: "Payment Status",
//         filterFn: fuzzyTextFilter,
//         enableGlobalFilter: true,
//       }),
//     columnHelper.display({
//       id: "orderStatus",
//       header: "Status",
//       cell: (info) => {
//         const order = info.row.original;
//         return (
//           <>
//             {order?.status === 100 ? "Draft" :
//               order?.status === 200 ? "Finalized" :
//                 order?.status === 300 ? "Scheduled" :
//                   order?.status === 400 ? "In Progress" :
//                     order?.status === 500 ? "Completed" :
//                       order?.status === 600 ? "Canceled" :
//                         "Unknown"}
//           </>
//         );
//       },
//     }),
//     columnHelper.accessor(
//       (row: any) => row?.payment_status || "",
//       {
//         header: "Production Status",
//         filterFn: fuzzyTextFilter,
//         enableGlobalFilter: true,
//       }),

//     columnHelper.accessor(
//       (row: any) => row?.payment_status || "",
//       {
//         header: "Production Location Status",
//         filterFn: fuzzyTextFilter,
//         enableGlobalFilter: true,
//       }),
//     columnHelper.accessor(
//       (row: any) => row?.payment_status || "",
//       {
//         header: "Priority",
//         filterFn: fuzzyTextFilter,
//         enableGlobalFilter: true,
//       }),
//     columnHelper.display({
//       id: "actions",
//       header: "Actions",
//       cell: (info) => {
//         const order = info.row.original;
//         return (
//           <>
//             <Link className="view" to={`/agent/jobs/viewInstallation/${order._id}`}>
//               <i className="ri-eye-fill fs-20"></i>
//             </Link>
//             {
//               !(order.status >= 200) && (
//                 <Link className="edit" to={`/agent/jobs/editInstallation/${order._id}`}>
//                   <i className="ri-pencil-fill fs-20"></i>
//                 </Link>
//               )
//             }
//             {
//               order.status === 500 &&
//               <Link
//                 className="btn btn-danger btn-sm ms-2"
//                 to={`/agent/jobs/addRemovalRequest/${order._id}`}
//               >
//                 Removal request
//               </Link>
//             }

//           </>
//         );
//       },
//     }),
//   ];
// }, [deleting]);